import React from 'react'
import {get, isEmpty, isNil} from 'lodash'
import moment from "moment"
import iconNames from '../theme/icons'
import TranslatedText from "components/translatedText"
import {getContactName} from 'helpers/contacts'
import Dates from 'helpers/dates'

export const ActivityStatus = {
  pending: 0,
  inProgress: 1,
  onHold: 2,
  rejected: 3,
  completed: 4,
}

export const getActivityStatusOptions = () => {
  return [
    {
      key: ActivityStatus.pending,
      value: ActivityStatus.pending,
      label: 'Pending',
    },
    {
      key: ActivityStatus.inProgress,
      value: ActivityStatus.inProgress,
      label: 'In Progress',
    },
    {
      key: ActivityStatus.onHold,
      value: ActivityStatus.onHold,
      label: 'On Hold',
    },
    {
      key: ActivityStatus.rejected,
      value: ActivityStatus.rejected,
      label: 'Rejected',
    },
    {
      key: ActivityStatus.completed,
      value: ActivityStatus.completed,
      label: 'Completed',
    },
  ]
}

export const isActivityRejected = (status) => {
  return status === ActivityStatus.rejected
}

export const ActivityBaseType = {
  call: -1,
  meeting: -2,
  toDo: -3,
  note: -4,
  task: -5,
  email: -6,
  quoteFollowUp: -7,
}

export const ActivityPriority = {
  high: 1,
  normal: 2,
  low: 3,
}

export const ActivityReasonType = {
  reject: 0,
}

export const isActivityReasonTypeRejected = (reasonType) => {
  return reasonType === ActivityReasonType.reject
}

export const getActivityRejectReasons = (reasons) => {
  return isEmpty(reasons)
    ? []
    : reasons.filter(reason => isActivityReasonTypeRejected(reason.type.id))
}

export const isRejectingActivityReasonEnabled = (activity, rejectReasons) => {
  return isActivityRejected(activity.status) && !isEmpty(rejectReasons)
}

export const PeriodUnit = {
  milliseconds: {value: 'milliseconds', label: <TranslatedText>{'Milliseconds'}</TranslatedText>, order: 0},
  seconds: {value: 'secs', label: <TranslatedText>{'Seconds'}</TranslatedText>, order: 1},
  minutes: {value: 'mins', label: 'Minutes', order: 2},
  hours: {value: 'hours', label: <TranslatedText>{'Hours'}</TranslatedText>, order: 3},
  days: {value: 'days', label: <TranslatedText>{'Days'}</TranslatedText>, order: 4},
  weeks: {value: 'weeks', label: <TranslatedText>{'Weeks'}</TranslatedText>, order: 5},
  months: {value: 'months', label: <TranslatedText>{'Months'}</TranslatedText>, order: 6},
  years: {value: 'years', label: <TranslatedText>{'Years'}</TranslatedText>, order: 7},
}

export const periodUnitsMinsToMonths = [
  PeriodUnit.minutes,
  PeriodUnit.hours,
  PeriodUnit.days,
  PeriodUnit.weeks,
  PeriodUnit.months,
]

export const periodUnitsDaysToYears = [
  PeriodUnit.days,
  PeriodUnit.weeks,
  PeriodUnit.months,
  PeriodUnit.years,
]

export const defaultAgendaActivityOptions = ['company', 'desc']

export const defaultAgendaActivityOptionsOrder = [
  'status',
  'type',
  'company',
  'contact',
  'desc',
  'owner',
  'allocated',
  'priority',
  'deadline',
  'ref',
]

export const defaultCalendarActivityOptions = ['start', 'end', 'contact', 'company', 'desc']

export const defaultCalendarActivityOptionsOrder = [
  {group: ['start', 'end']},
  'contact',
  'desc',
  'company',
  'owner',
  'ref',
]

// Returns the status color based on the activity's status
export const getActivityStatusColorForStatus = (status) => {
  const themeVars = typeof window !== 'undefined' ? {
    pending: getComputedStyle(document.documentElement).getPropertyValue('--cg-color-activity-status-pending').trim() || '#ffc107',
    inProgress: getComputedStyle(document.documentElement).getPropertyValue('--cg-color-activity-status-in-progress').trim() || '#3f51b5',
    onHold: getComputedStyle(document.documentElement).getPropertyValue('--cg-color-activity-status-on-hold').trim() || '#b30000',
    rejected: getComputedStyle(document.documentElement).getPropertyValue('--cg-color-activity-status-rejected').trim() || '#f44336',
    completed: getComputedStyle(document.documentElement).getPropertyValue('--cg-color-activity-status-completed').trim() || '#4caf50',
  } : {
    pending: '#ffc107',
    inProgress: '#3f51b5',
    onHold: '#b30000',
    rejected: '#f44336',
    completed: '#4caf50',
  }

  switch (status) {
    case ActivityStatus.pending:
      return themeVars.pending
    case ActivityStatus.inProgress:
      return themeVars.inProgress
    case ActivityStatus.onHold:
      return themeVars.onHold
    case ActivityStatus.rejected:
      return themeVars.rejected
    case ActivityStatus.completed:
      return themeVars.completed
    default:
      return themeVars.pending
  }
}

// Returns the activity's status title based on the status
export const getActivityStatusName = (status) => {
  let statusTitle
  switch (status) {
    case ActivityStatus.pending:
      statusTitle = 'Pending'
      break
    case ActivityStatus.inProgress:
      statusTitle = 'In Progress'
      break
    case ActivityStatus.onHold:
      statusTitle = 'On Hold'
      break
    case ActivityStatus.rejected:
      statusTitle = 'Rejected'
      break
    case ActivityStatus.completed:
      statusTitle = 'Completed'
      break
    default:
      statusTitle = ''
      break
  }
  return statusTitle
}

export const getActivityIcon = (activity) =>
  getActivityIconNameForType(get(activity, 'activitytype.itemType'))

// Returns the activity icon name based on the activity base type
export const getActivityIconNameForType = (baseType) => {
  switch (baseType) {
    case ActivityBaseType.call:
      return iconNames.activityCall
    case ActivityBaseType.meeting:
      return iconNames.activityMeeting
    case ActivityBaseType.toDo:
      return iconNames.activityTodo
    case ActivityBaseType.note:
      return iconNames.activityNote
    case ActivityBaseType.task:
      return iconNames.activityTask
    case ActivityBaseType.email:
      return iconNames.activityEmail
    case ActivityBaseType.quoteFollowUp:
      return iconNames.order
    default:
      return iconNames.activity
  }
}

export const getActivityIconName = (activityType) => {
  return getActivityIconNameForType(activityType?.fixedType ? activityType?.fixedType : activityType?.itemType)
}

export const getTypesForUser = (types, user) => {
  if (!isTaskAvailable(user)) types = types.filter(type => type.itemType !== ActivityBaseType.task)
  if (!isEmailAvailable(user)) types = types.filter(type => type.itemType !== ActivityBaseType.email)
  return types
}

export const isTaskAvailable = (user) => (!user.proSync || user.proIntVersion >= 5001000000)
export const isEmailAvailable = (user) => (!user.proSync || user.proIntVersion >= 5003001000)

// Returns the default activity description based on the activity type
export const getDefaultActivityDescForType = (baseType) => {
  let activityDescription
  switch (baseType) {
    case ActivityBaseType.call:
      activityDescription = 'New Call'
      break
    case ActivityBaseType.meeting:
      activityDescription = 'New Meeting'
      break
    case ActivityBaseType.toDo:
      activityDescription = 'New To Do'
      break
    case ActivityBaseType.note:
      activityDescription = 'New Note'
      break
    case ActivityBaseType.task:
      activityDescription = 'New Task'
      break
    case ActivityBaseType.email:
      activityDescription = 'New Email'
      break
    default:
      activityDescription = 'New Activity'
      break
  }
  return activityDescription
}

// Returns the activity's priority title based on the priority
export const getActivityPriorityTitle = (priority) => {
  let priorityTitle
  switch (priority) {
    case ActivityPriority.high:
      priorityTitle = 'High'
      break
    case ActivityPriority.normal:
      priorityTitle = 'Normal'
      break
    case ActivityPriority.low:
      priorityTitle = 'Low'
      break
    default:
      priorityTitle = 'Low'
      break
  }
  return priorityTitle
}

export const getAttendeeNames = (attendees) => {
  return isEmpty(attendees)
    ? []
    : attendees
      .map(attendee => attendee.contact)
      .map(getContactName)
      .join(', ')
}

export const getTimeSpent = (activity) => {
  if (activity && activity.activityUpdates) {
    const timeSpent = activity.activityUpdates.reduce((total, update) => total + update.timeSpent, 0)
    const d = Number(timeSpent)
    const h = Math.floor(d / 60)
    const m = Math.floor(d % 60)

    let ret = ''
    if (h > 0) ret += (h + (h === 1 ? ' Hour' : ' Hours'))
    if (h > 0 && m > 0) ret += ', '
    if (m > 0) ret += (m + (m === 1 ? ' Minute' : ' Minutes'))
    return ret
  } else {
    return ''
  }
}

export const getOwnerName = (activity) => {
  if (activity !== null && activity !== undefined) {
    if (activity.owner && activity.owner.length > 0) {
      const owner = activity.owner[0].contact
      if (owner) return `${owner.forename} ${owner.surname}`
    }
  }
  return ''
}

export const getActivitiesContainerHeightStyle = (window, containerOffset) => {
  return {height: window.innerHeight - containerOffset.top}
}

export const getDefaults = (defaultData, selectedCompany, selectedContact, selectedCompanyMainContact) => {
  const company = defaultData.company || selectedCompany
  const contact = defaultData.contact || selectedContact || selectedCompanyMainContact
  const order = defaultData.order
  const opportunity = get(defaultData, 'opportunity', null)
  return {
    company,
    contact,
    order,
    opportunity,
  }
}

export const getActivityDate = (activity, dateKey) => {
  return get(activity, 'activitytype.itemType', null) === ActivityBaseType.note
    ? get(activity, 'modifiedDate', null)
    : get(activity, dateKey, null)
}

export const getActivityTitle = (event) => {
  if (isNil(event))
    return null

  const activeView = get(event, 'activeView', null)
  const startTime = get(event, 'startTime', null)
  const startDate = get(event, 'startDate', null)
  const endTime = get(event, 'endTime', null)
  const endDate = get(event, 'endDate', null)
  const isAllDay = get(event, 'allDay', null)
  const contact = get(event, 'contact', null)
  const company = get(event, 'company', null)
  const desc = get(event, 'desc', null)
  const owner = get(event, 'owner', null)
  const reference = get(event, 'reference', null)

  const isMonthView = activeView.includes('month')
  const isMultiDayEvent = !moment(startDate).isSame(moment(endDate), 'date')

  const activity = [
    contact,
    company,
    desc,
    owner,
    reference,
  ]
  const details = activity
    .filter(value => !isNil(value))
    .filter(value => !isEmpty(value))
    .join(' - ')

  const times = endTime
    ? `${startTime}-${endTime}: `
    : `${startTime}: `

  return isAllDay || !startTime || !isMonthView || isMultiDayEvent
    ? details
    : times + details
}

export const getActivityTitleCalendar = (event, order) => {
  if (isNil(event))
    return null
  const getValue = (field) => get(event, field, null)
  const fields = {
    'status': getValue('statusLabel'),
    'type': getValue('type'),
    'owner': getValue('owner'),
    'start': get(event, 'startTime', null),
    'end': get(event, 'endTime', null),
    'contact': getValue('contact'),
    'desc': getValue('desc'),
    'company': getValue('company'),
    'allocated': getValue('attendees'),
    'priority': getValue('priority'),
    'deadline': getValue('deadline'),
    'ref': getValue('reference'),
  }
  const fieldsInOrder = []
  order.forEach((item) => {
    if (!isNil(item)) {
      if (item.hasOwnProperty('group')) {
        item.group.forEach((field) => fieldsInOrder.push(fields[field]))
      } else {
        fieldsInOrder.push(fields[item])
      }
    }

  })
  return fieldsInOrder
    .filter((value) => !isNil(value))
    .filter((value) => !isEmpty(value))
    .join(' - ')
}

export const getDateTime = (event, prefix) => {
  if (isNil(event))
    return null

  const time = event.allDay === true
    ? 'All Day'
    : Dates.getTimeString(Dates.getDateTimeFromUnix(get(event, `${prefix}Date`, null)))
  const date = new Date(get(event, `${prefix}Date`, null)).toLocaleDateString('en-GB')
  return `${date} - ${time}`
}

export const getEventById = (events, event) => {
  return events.find(obj => obj.id === event.id) || null
}

export const getIsActivityOptionChecked = (options, field) => {
  if (isNil(options))
    return false

  return options.includes(field)
}

export const toggleSelectedActivityType = (selectedActivityTypes, value) => {
  if (!selectedActivityTypes.includes(value)) {
    return [...selectedActivityTypes, value]
  } else {
    return selectedActivityTypes.filter((v) => v !== value)
  }
}

export const isActivityTypeSelected = (selectedActivityTypes, option) => {
  return selectedActivityTypes.includes(option.value)
}

export const getUpdatedOptionsOrder = (
  destination,
  source,
  selectedViewType,
  agendaOptionOrder,
  activityOptionOrder
) => {
  const newOptions =
    selectedViewType === "agenda"
      ? [...agendaOptionOrder]
      : [...activityOptionOrder]

  const [removed] = newOptions.splice(source.index, 1)
  newOptions.splice(destination.index, 0, removed)

  return newOptions
}

export default {
  ActivityBaseType,
  ActivityStatus,
  ActivityPriority,
  defaultAgendaActivityOptions,
  defaultAgendaActivityOptionsOrder,
  defaultCalendarActivityOptions,
  defaultCalendarActivityOptionsOrder,
  getActivityStatusColorForStatus,
  getActivityStatusName,
  getActivityIconNameForType,
  getDefaultActivityDescForType,
  getActivityPriorityTitle,
  getAttendeeNames,
  getTimeSpent,
  getOwnerName,
  getTypesForUser,
  isTaskAvailable,
  isEmailAvailable,
  getDefaults,
  getIsActivityOptionChecked,
  getEventById,
  toggleSelectedActivityType,
  isActivityTypeSelected,
  getUpdatedOptionsOrder,
}
